import { ListItem, Text, UnorderedList, VStack } from "@chakra-ui/react";
import { FC, type ReactNode } from "react";

import type { WorkspacePendingSummary } from "./moduleWorkspacePendingSummary";

type Props = {
  summary: WorkspacePendingSummary;
  /** When true, explains that deletions are not listed as pending removals */
  showRemovalNote?: boolean;
};

export const PendingChangeSummaryLists: FC<Props> = ({
  summary,
  showRemovalNote = false,
}) => {
  return (
    <VStack align="stretch" spacing={3}>
      <BoxSection title="コードを変更したモジュール（下書き）">
        {summary.codeDraftModuleNames.length === 0 ? (
          <Text fontSize="sm" color="gray.600">
            なし
          </Text>
        ) : (
          <UnorderedList spacing={1} pl={4} fontSize="sm">
            {summary.codeDraftModuleNames.map((name) => (
              <ListItem key={`code-${name}`}>{name}</ListItem>
            ))}
          </UnorderedList>
        )}
      </BoxSection>

      <BoxSection title="有効/無効の予約">
        {summary.toggleChanges.length === 0 ? (
          <Text fontSize="sm" color="gray.600">
            なし
          </Text>
        ) : (
          <UnorderedList spacing={1} pl={4} fontSize="sm">
            {summary.toggleChanges.map((t) => (
              <ListItem key={`tog-${t.moduleName}`}>
                {t.moduleName}: {t.fromLabel} → {t.toLabel}
              </ListItem>
            ))}
          </UnorderedList>
        )}
      </BoxSection>

      <BoxSection title="配信条件連動の予約">
        {summary.linkChanges.length === 0 ? (
          <Text fontSize="sm" color="gray.600">
            なし
          </Text>
        ) : (
          <UnorderedList spacing={1} pl={4} fontSize="sm">
            {summary.linkChanges.map((l) => (
              <ListItem key={`link-${l.moduleName}`}>
                {l.moduleName}: {l.fromLabel} → {l.toLabel}
              </ListItem>
            ))}
          </UnorderedList>
        )}
      </BoxSection>

      <BoxSection title="前回のスナップショット以降に追加されたモジュール">
        {summary.addedModuleNames.length === 0 ? (
          <Text fontSize="sm" color="gray.600">
            なし
          </Text>
        ) : (
          <UnorderedList spacing={1} pl={4} fontSize="sm">
            {summary.addedModuleNames.map((name) => (
              <ListItem key={`add-${name}`}>{name}</ListItem>
            ))}
          </UnorderedList>
        )}
      </BoxSection>

      <BoxSection title="削除されたモジュール">
        {summary.removedModuleNames.length === 0 ? (
          <Text fontSize="sm" color="gray.600">
            {showRemovalNote
              ? "この画面からは検出できません（削除は即時に反映されます）。"
              : "なし"}
          </Text>
        ) : (
          <UnorderedList spacing={1} pl={4} fontSize="sm">
            {summary.removedModuleNames.map((name) => (
              <ListItem key={`rm-${name}`}>{name}</ListItem>
            ))}
          </UnorderedList>
        )}
      </BoxSection>
    </VStack>
  );
};

const BoxSection: FC<{ title: string; children: ReactNode }> = ({
  title,
  children,
}) => (
  <VStack align="stretch" spacing={1}>
    <Text fontSize="sm" fontWeight="semibold" color="gray.700">
      {title}
    </Text>
    {children}
  </VStack>
);
